import React from 'react';
import { Icon, Spinner, Typography } from '../common';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { GET_DASHBOARD_SEARCH_RESULTS } from '@/api';
import { dashboardApplicationsService } from '@/api/dashboardApplication';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { useRouter } from 'next/router';
import Icons from '@/const/icons';
import Link from 'next/link';
import { InputText } from '../form';
import { useForm } from 'react-hook-form';

interface MobileSearchModalContentProps {
	hide: () => void;
}
type menuOption = 'client' | 'case' | undefined;
interface User {
	user_id: string;
	full_name: string;
	email: string;
	cases: Case[];
}
interface Case {
	case_id: string;
	case_name: string;
}
const MobileSearchModalContent: React.FC<MobileSearchModalContentProps> = ({
	hide,
}) => {
	const router = useRouter();
	const { register } = useForm();
	const [search, setSearch] = React.useState<string>('');
	const [searchFilter] = useDebounce(search, 500);
	const [users, setUsers] = React.useState<User[]>([]);
	const [cases, setCases] = React.useState<Case[]>([]);
	const [selectedOption, setSelectedOption] = React.useState<menuOption>();
	const [selectedUser, setSelectedUser] = React.useState<User | undefined>();
	const [selectedCase, setSelectedCase] = React.useState<Case | undefined>();
	const [loading, setLoading] = React.useState<boolean>(false);
	const { data: searchData, isFetching } = useQuery({
		queryKey: [GET_DASHBOARD_SEARCH_RESULTS, searchFilter],
		queryFn: () =>
			dashboardApplicationsService.getClientAndCasesList(searchFilter),
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		const cases = searchData?.data.cases;
		const users = searchData?.data.users;
		if (cases) {
			setCases(cases);
		} else {
			setCases([]);
		}

		if (users) {
			setUsers(users);
		} else {
			setUsers([]);
		}
	}, [searchData]);

	const highlightText = (text: string) => {
		if (!text) return '';
		if (!search) {
			return <span style={{ opacity: 0.6 }}>{text}</span>;
		}

		const parts = text.split(new RegExp(`(${search})`, 'gi'));
		return parts.map((part, index) => {
			const isMatch = part.toLowerCase() === search.toLowerCase();
			return (
				<span key={index} style={{ opacity: isMatch ? 1 : 0.6 }}>
					{part}
				</span>
			);
		});
	};
	const isHighlighted = (text: string) => {
		if (!search || !text) return false; // If no search term, no highlight
		return text.toLowerCase().includes(search.toLowerCase());
	};

	const handleSelectUser = async (userName: string) => {
		try {
			setLoading(true);
			const response = await dashboardApplicationsService.getClientAndCasesList(
				userName
			);
			if (response.data) {
				const cases = response.data.cases;
				const user = response.data.users[0];
				setSelectedUser({
					cases: cases,
					full_name: user.full_name,
					email: user.email,
					user_id: user.user_id,
				});
				setSelectedOption('client');
				setSelectedCase(undefined);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};
	const handleSelectCase = async (caseName: string) => {
		try {
			setLoading(true);
			const response = await dashboardApplicationsService.getClientAndCasesList(
				caseName
			);
			if (response.data) {
				setSelectedCase(response.data.cases[0]);
				setSelectedUser(undefined);
				setSelectedOption('case');
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};
	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<Typography type="semibold-14">Search</Typography>
				<XMarkIcon className="text-black w-6 h-6" onClick={hide} />
			</div>
			<InputText
				name="search"
				register={register}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				classNameInput="h-[37px] !mt-0 !rounded-[100px] !border-[#0D061614] !pl-10"
				className="mb-6"
				iconLeft={Icons.search}
				classNameIcon="!h-4 !w-4 !text-opacity-black !top-[30%]"
				iconRight={Icons.closeCircle}
				rightClick={() => setSearch('')}
			/>
			<div>
				{isFetching || loading ? (
					<div className="flex justify-center  py-6">
						<Spinner type="loadingPage" />
					</div>
				) : (
					<>
						{selectedOption && (
							<div className="pb-4 border-b border-gray-100 ">
								<div
									className="flex gap-3 w-fit cursor-pointer items-center"
									onClick={() => setSelectedOption(undefined)}
								>
									<Icon className="!h-4 !w-4" src={Icons.arrowLeft} />
									<Typography type="semibold-14">Back</Typography>
								</div>
							</div>
						)}
						{selectedOption === 'client' && (
							<div className="mt-8">
								{selectedUser && selectedUser.cases.length > 0 && (
									<>
										<div className="flex gap-3 w-fit  items-center mb-4">
											<Icon
												className="!h-4 !w-4 !text-primary"
												src={Icons.suitcaseThick}
											/>
											<Typography type="semibold-14">Cases</Typography>
										</div>
										<ul className="flex flex-col gap-1 list-none max-h-[130px] overflow-y-auto scroll-custom mb-8">
											{selectedUser.cases.map((item, index) => (
												<Link
													href={`/lawyerapp/cases/${item.case_id}?tab=case-clients`}
													key={`${item.case_id}- ${index}`}
													className="flex items-center gap-2 py-1 px-2 hover:bg-gray-5 cursor-default opacity-60"
													onClick={hide}
												>
													<span>•</span>
													<Typography type="regular-14">
														{item.case_name}
													</Typography>
												</Link>
											))}
										</ul>
									</>
								)}
								<div
									className="flex gap-3 w-fit  cursor-pointer items-center"
									onClick={() => {
										if (selectedUser) {
											router.push(
												`/lawyerapp/client-profile/${selectedUser.user_id}`
											);
											hide();
										}
									}}
								>
									<Icon
										className="!h-4 !w-4 !text-primary"
										src={Icons.profile}
									/>
									<Typography type="semibold-14">Go to Profile</Typography>
									<Icon
										className="!h-4 !w-4 !rotate-180"
										src={Icons.arrowLeft}
									/>
								</div>
							</div>
						)}
						{selectedOption === 'case' && (
							<div className="mt-8">
								<div
									className="flex gap-3 w-fit  cursor-pointer items-center"
									onClick={() => {
										if (selectedCase) {
											router.push(`/lawyerapp/cases/${selectedCase.case_id}`);
										}
										hide();
									}}
								>
									<Icon
										className="!h-4 !w-4 !text-primary"
										src={Icons.suitcaseThick}
									/>
									<Typography type="semibold-14">Go to Case Details</Typography>
									<Icon
										className="!h-4 !w-4 !rotate-180"
										src={Icons.arrowLeft}
									/>
								</div>
							</div>
						)}
						{!selectedOption && (
							<>
								{users.length > 0 && (
									<div className="mb-4">
										<Typography type="semibold-12" className="mb-1 text-start">
											Clients
										</Typography>
										<ul className="flex flex-col gap-1 list-none max-h-[110px] overflow-y-auto scroll-custom">
											{users.map((client, index) => (
												<li
													key={`${client.user_id}- ${index}`}
													className="flex items-center  text-start gap-2 py-1 px-2 hover:bg-gray-5  cursor-default"
													onClick={() => {
														handleSelectUser(client.full_name);
													}}
												>
													<span
														style={{
															opacity: isHighlighted(client.full_name)
																? 1
																: 0.6, // Dot opacity changes based on search result in case title
														}}
													>
														•
													</span>
													<Typography type="regular-14">
														{highlightText(client.full_name)}
													</Typography>
												</li>
											))}
										</ul>
									</div>
								)}

								{cases.length > 0 && (
									<div className="mb-4">
										<Typography type="semibold-12" className="mb-1  text-start">
											Cases
										</Typography>

										<ul className="flex flex-col gap-1 list-none max-h-[110px] overflow-y-auto scroll-custom">
											{cases.map((item, index) => (
												<li
													key={`${item.case_id}- ${index}`}
													className="flex items-center  gap-2 py-1 px-2 hover:bg-gray-5  text-start cursor-default"
													onClick={() => handleSelectCase(item.case_name)}
												>
													<span
														style={{
															opacity: isHighlighted(item.case_name) ? 1 : 0.6,
														}}
													>
														•
													</span>
													<Typography type="regular-14">
														{highlightText(item.case_name)}
													</Typography>
												</li>
											))}
										</ul>
									</div>
								)}
								{search && cases.length === 0 && users.length === 0 && (
									<div className="bg-gray-20 h-[158px] border-dashed border border-[#0d061614] rounded-2xl flex justify-center items-center">
										<Typography type="regular-16" className="opacity-60">
											<p className="text-center">No matches found.</p>
											<p className="text-center">
												Please refine your search and try again.
											</p>
										</Typography>
									</div>
								)}
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default MobileSearchModalContent;
